import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { TemperMark } from "../components/Logo";
import { MaskedLines, FadeUp, ParallaxImage } from "../components/Reveal";
import Seo from "../components/Seo";

const COACHING =
  "https://images.unsplash.com/photo-1544486927-c903fcc7dcc3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwzfHxvbmUlMjBvbiUyMG9uZSUyMGNvYWNoaW5nJTIwY29udmVyc2F0aW9uJTIwdHdvJTIwbWVuJTIwdGFsa2luZyUyMG1pbmltYWx8ZW58MHx8fHwxNzkxNDgxNjA3fDA&ixlib=rb-4.1.0&q=85";
const TEAM =
  "https://images.unsplash.com/photo-1758518729685-f88df7890776?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHw0fHxidXNpbmVzcyUyMHRlYW0lMjBjb2xsYWJvcmF0aW9uJTIwbW9kZXJuJTIwb2ZmaWNlfGVufDB8fHx8MTc5MTQ4MDk1NXww&ixlib=rb-4.1.0&q=85";
const ARCH =
  "https://images.unsplash.com/photo-1738844153732-a485f0e78382?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMG1pbmltYWwlMjBhcmNoaXRlY3R1cmUlMjBzaGFkb3d8ZW58MHx8fHwxNzkxNDgwOTU1fDA&ixlib=rb-4.1.0&q=85";

const SERVICES = [
  {
    n: "01",
    id: "coaching",
    title: "One-on-One Leadership Coaching",
    tagline: "Private, candid coaching for leaders who carry real weight.",
    desc: "No curriculum, no platitudes. We start with an honest read of where you actually lead — then work the real decisions, dynamics, and pressures on your desk until your response to them changes.",
    includes: [
      "Private working sessions, on a cadence that fits your calendar",
      "A TEMPER Profile assessment to locate your strengths and your edge",
      "Direct feedback on the decisions and dynamics you're actually facing",
      "Practice between sessions — real work, not worksheets",
    ],
    built: "Built for leaders at every level — from new manager and supervisor training to executive coaching for founders and senior leaders.",
    img: COACHING,
  },
  {
    n: "02",
    id: "clinics",
    title: "Team Building Clinics",
    tagline: "Rooms where teams practice trust, candor, and repair.",
    desc: "Clinics are working sessions, not off-site theater. Your team practices the conversations it avoids — feedback, disagreement, repair — on real scenarios from your real work. It's the fastest way to invest in employee engagement and workplace culture, because culture is built in conversations, not posters.",
    includes: [
      "Facilitated sessions built around your team's real work — not trust falls",
      "Scenario practice for the conversations your team keeps avoiding",
      "A shared language for pressure, feedback, and follow-through",
      "Formats for leadership teams, departments, and off-sites",
    ],
    built: "Built for teams that work hard together and want to work well together.",
    img: TEAM,
  },
  {
    n: "03",
    id: "consulting",
    title: "Organizational Consulting",
    tagline: "Clear-eyed counsel for leadership that works at every level.",
    desc: "When leadership problems are structural, coaching one person won't fix them. This is organizational development consulting: we diagnose how leadership actually moves through your organization — and how it shapes workplace culture and employee engagement — then counsel you on what to change, honestly and practically.",
    includes: [
      "An honest diagnosis of how leadership currently moves through your organization",
      "Practical counsel on structure, roles, and decision-making",
      "Support through transitions, growth, and change",
      "A confidential sounding board for executives and boards",
    ],
    built: "Built for organizations that have outgrown their current way of leading.",
    img: ARCH,
  },
];

const PROCESS = [
  { n: "01", t: "Diagnose", d: "Honest assessment first — where the heat actually is." },
  { n: "02", t: "Temper", d: "Deliberate practice on the real thing, with real feedback." },
  { n: "03", t: "Forge", d: "Tested under pressure until it holds without you in the room." },
];

export default function Services() {
  return (
    <>
      <Seo
        title="Leadership Coaching, Team Building & Consulting | TEMPER"
        description="One-on-one leadership coaching, corporate team building clinics, and organizational development consulting — practical work built to hold under real pressure."
        path="/services"
      />
      <section data-testid="services-header" className="pt-32 md:pt-44">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10">
          <FadeUp>
            <p className="eyebrow flex items-center gap-3 text-ink/60">
              <TemperMark className="h-3 w-3 text-ember" /> Services
            </p>
          </FadeUp>
          <MaskedLines
            as="h1"
            className="mt-8 font-display text-[clamp(3rem,7.5vw,7rem)] font-black leading-[0.95] tracking-[-0.03em]"
            lines={["Work that holds", <span key="x">under <span className="text-ember">pressure.</span></span>]}
          />
          <FadeUp className="mt-8 max-w-xl" delay={0.15}>
            <p className="text-lg leading-relaxed text-steel">
              Three ways to work together. One standard: leadership that survives contact
              with reality.
            </p>
          </FadeUp>
        </div>
      </section>

      {SERVICES.map((s, i) => (
        <section
          key={s.id}
          id={s.id}
          data-testid={`service-section-${s.id}`}
          className="mt-24 border-t border-line py-16 md:mt-32 md:py-24"
        >
          <div className="mx-auto grid max-w-[1440px] gap-12 px-6 md:grid-cols-12 md:px-10">
            <div className={`md:col-span-5 ${i % 2 === 1 ? "md:order-2" : ""}`}>
              <FadeUp>
                <span className="font-mono text-[11px] tracking-[0.2em] text-ember">{s.n}</span>
                <h2 className="mt-5 font-display text-3xl font-extrabold leading-tight tracking-tight md:text-5xl">
                  {s.title}
                </h2>
                <p className="mt-4 font-display text-xl text-steel md:text-2xl">{s.tagline}</p>
                <p className="mt-6 leading-relaxed text-steel">{s.desc}</p>
              </FadeUp>
              <FadeUp className="mt-10" delay={0.1}>
                <p className="eyebrow text-ink/50">Inside the work</p>
                <ul className="mt-5 space-y-4">
                  {s.includes.map((inc) => (
                    <li key={inc} className="flex items-start gap-3 text-ink/80">
                      <TemperMark className="mt-1.5 h-3 w-3 shrink-0 text-ember" />
                      <span className="leading-relaxed">{inc}</span>
                    </li>
                  ))}
                </ul>
              </FadeUp>
              <FadeUp className="mt-10" delay={0.15}>
                <p className="border-l-2 border-ember pl-5 text-lg leading-relaxed text-ink">
                  {s.built}
                </p>
              </FadeUp>
            </div>
            <div className={`md:col-span-7 ${i % 2 === 1 ? "md:order-1" : ""}`}>
              <FadeUp delay={0.1} className="h-full">
                <ParallaxImage
                  src={s.img}
                  alt={s.title}
                  className="h-72 w-full md:h-full md:min-h-[480px]"
                  caption={`Fig. 0${i + 2} — ${s.title}`}
                />
              </FadeUp>
            </div>
          </div>
        </section>
      ))}

      <section data-testid="services-process" className="bg-coal py-24 text-paper md:py-36">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10">
          <FadeUp>
            <p className="eyebrow text-ember">How the work runs</p>
          </FadeUp>
          <MaskedLines
            className="mt-8 font-display text-4xl font-extrabold tracking-tight md:text-6xl"
            lines={["Diagnose. Temper. Forge."]}
          />
          <div className="mt-16 grid gap-px border border-paper/15 bg-paper/15 md:grid-cols-3">
            {PROCESS.map((p, i) => (
              <FadeUp key={p.n} delay={i * 0.08} className="bg-coal p-8 md:p-10">
                <span className="font-mono text-[11px] tracking-[0.2em] text-ember">{p.n}</span>
                <h3 className="mt-5 font-display text-3xl font-extrabold tracking-tight">{p.t}</h3>
                <p className="mt-4 leading-relaxed text-paper/60">{p.d}</p>
              </FadeUp>
            ))}
          </div>
          <FadeUp className="mt-14">
            <Link to="/contact" data-testid="services-contact-cta" className="btn-ember">
              Bring the work to your team <ArrowRight className="h-4 w-4" />
            </Link>
          </FadeUp>
        </div>
      </section>
    </>
  );
}
